import React, { useEffect, useState, useContext } from "react";
import { useParams } from 'react-router';
import Screen from '../components/Screen';
import { getCDNImage } from '../globals';
import { currencyFormat, i18nValue, priceWithoutTax } from '../functions';
import { UserContext } from "../context/user";
import { HotelContext } from "../context/hotel";
import useFoodMenu from "../hooks/useFoodMenu";
import { usePersistentState } from '../hooks/usePersistentState';
import { useLocalStorage } from "@penseapp/uselocalstorage";
import { ItemGlutenFree, ItemVegan, ItemVegatarian } from "../components/Icons";
import { T, TF } from "react-translator-component";
import { hotelShowsPriceWithoutTaxes } from "../components/CheckIn/hotelConditions";
import { useLanguage } from "../hooks/useLanguage";

export const FoodMenu = () => {
  const { data: hotel, cacheVersion, currency } = useContext(HotelContext);
  const { restaurantId, id } = useParams();
  const { language } = useContext(UserContext);
  // const [data, setData] = usePersistentState(`menu-${id}-${cacheVersion}`, null, hotel?.code === "comahue" ? 1440 : 30)
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // useLocalStorage(`${hotel.id}-menu-${id}-${cacheVersion}`, null, 60 * 30);
  const { getFoodMenu } = useFoodMenu();

  // 1. Estados para buscador y filtros unificados
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  let hasTaxes = hotelShowsPriceWithoutTaxes(hotel.id);
  const { defaultLanguage } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getFoodMenu(restaurantId, id);
        setData(result);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch food menu:', error);
        setLoading(false);
      }
    };
    if (!data) fetchData();
  }, [restaurantId, id]);

  // 2. Función para obtener todas las opciones de filtro disponibles
  const getAvailableFilters = () => {
    if (!data?.content) return [];
    
    const filters = [
      { id: "all", name: T("menu.all"), type: "all" }
    ];
    
    // Agregar categorías
    data.content.forEach((section, idx) => {
      filters.push({
        id: `category-${idx}`,
        name: i18nValue(section, "title", language, null, null, defaultLanguage()) || TF("menu.category", [idx + 1]),
        type: "category",
        categoryIndex: idx
      });
    });
    
    // Agregar etiquetas especiales
    const allTags = new Set();
    data.content.forEach(section => {
      if (section.items) {
        section.items.forEach(item => {
          if (item.tags) {
            item.tags.forEach(tag => allTags.add(tag));
          }
        });
      }
    });
    
    allTags.forEach(tag => {
      // Solo incluir vegetarian y gluten-free
      if (tag === "vegetarian" || tag === "gluten-free") {
        let iconComponent = null;
        let displayName = tag;
        
        if (tag === "vegetarian") {
          iconComponent = <ItemVegatarian width={20} height={20} />;
          displayName = T("menu.vegetarian");
        } else if (tag === "gluten-free") {
          iconComponent = <ItemGlutenFree width={20} height={20} />;
          displayName = T("menu.gluten_free");
        }
        
        filters.push({
          id: `tag-${tag}`,
          name: displayName,
          icon: iconComponent,
          type: "tag",
          tagValue: tag
        });
      }
    });
    
    return filters;
  };

  // 3. Función para filtrar items por búsqueda y filtros unificados
  const filterItems = (items) => {
    let filteredItems = items;
    
    // Filtro por término de búsqueda
    if (searchTerm) {
      filteredItems = filteredItems.filter(item => {
        const name = i18nValue(item, "name", language) || item.name || "";
        const desc = i18nValue(item, "description", language) || item.description || "";
        return (
          name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          desc.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    
    // Filtro por filtro activo
    if (activeFilter !== "all") {
      const currentFilter = getAvailableFilters().find(f => f.id === activeFilter);
      if (currentFilter) {
        if (currentFilter.type === "tag") {
          filteredItems = filteredItems.filter(item => {
            return item.tags && item.tags.includes(currentFilter.tagValue);
          });
        }
        // Para categorías, el filtrado se maneja en el renderizado principal
      }
    }
    
    return filteredItems;
  };


  if (loading) {
    return <div className="loading text-center mt-5"><div className="spinner-border"></div></div>
  }

  if (!data) {
    return <div className="text-center mt-5">Not Found</div>
  }

  const Item = ({ reg }) => {

    const isVegan = reg.tags && reg.tags?.includes("vegan")
    const isVegetarian = reg.tags && reg.tags?.includes("vegetarian")
    const isGlutenFree = reg.tags && reg.tags?.includes("gluten-free")

    return <div key={`foodMenu-item-${reg.id}`} className="product-item d-flex bg-white shadow mb-3">
      <div className="product-item-body d-flex flex-column flex-grow-1 align-self-center">
        <h6 className="product-item-name">
          {i18nValue(reg, "name", language)}
          {isVegan && (
            <span style={{ display: "inline-block", marginLeft: 2 }}>
              <ItemVegan width={22} height={22} />
            </span>
          )}
          {isVegetarian && (
            <span style={{ display: "inline-block", marginLeft: 2 }}>
              <ItemVegatarian width={22} height={22} />
            </span>
          )}
          {isGlutenFree && (
            <span style={{ display: "inline-block", marginLeft: 2 }}>
              <ItemGlutenFree width={22} height={22} />
            </span>
          )}
        </h6>
        <p className="product-item-description mb-0">
          {i18nValue(reg, "description", language)}
        </p>
        {reg.price > 0 && (
          <span className="product-item-price" style={{ fontWeight: '400' }}>
            <b>{currencyFormat(reg.price, currency)}</b>
          </span>
        )}
        {hasTaxes && reg.price > 0 && (
          <div className="d-flex justify-content-between align-items-center">
              <span className="product-item-price-tax">
                <small className="fontSize-small" style={{ fontSize: '0.68rem', fontStyle: 'italic', color: '#a5a5a5' }}>{T("menu.price_without_taxes")} {currencyFormat(priceWithoutTax(reg.price), currency)}</small>
            </span>
          </div>
        )}
      </div>
      {reg.photo && (
        <img
          src={getCDNImage(reg.photo)}
          className="product-item-photo rounded-lg"
          alt={reg.name}
        />
      )}
    </div>
  }

  return (
    <Screen
      section="food-menu"
      title={i18nValue(data, "name", language) || 'Menu'}
      classes="bg-white"
    >
      <p className="page-description">{i18nValue(data, "description", language)}</p>
      {/* Buscador y Tabs */}
      {data.content && data.content.length > 0 && (
        <div style={{ paddingBottom: 10, marginBottom: 20, borderBottom: "1px solid #ddd" }}>
          <input
            type="text"            
            className="form-control mb-3 text-center pt-3 pb-3 input-menu-search"
            placeholder={T("menu.search")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          
                     {/* Filtros unificados */}
           <div className="mb-3">
             <div className="d-flex flex-wrap justify-content-center" style={{ gap: 8 }}>
               {getAvailableFilters().map(filter => (
                 <button
                   key={filter.id}
                   type="button"
                   className={`btn btn-sm pt-2 pb-2 d-flex align-items-center justify-content-center ${
                     activeFilter === filter.id 
                       ? 'btn-secondary' 
                       : 'btn-outline-secondary'
                   }`}
                   style={{ 
                     minWidth: filter.type === "tag" ? 80 : 100, 
                     fontSize: '12px',
                     padding: '8px 12px',
                     borderWidth: '1px',
                     borderStyle: 'solid',
                     borderColor: activeFilter === filter.id 
                       ? "#697279" 
                       : "#ccc",
                     transition: 'all 0.2s ease',
                     fontSize: '0.875rem',
                   }}
                   onClick={() => setActiveFilter(filter.id)}
                 >
                  <span style={{ marginRight: filter.icon ? '5px' : '0' }}>{filter.icon && filter.icon}</span>
                  {filter.name} 
                 </button>
               ))}
             </div>
           </div>
        </div>
      )}
      {/* Renderizado de items basado en el filtro activo */}
      {data.content && data.content.length > 0 && (
        (() => {
          const currentFilter = getAvailableFilters().find(f => f.id === activeFilter);
          
          if (activeFilter === "all" || !currentFilter) {
            // Mostrar todas las categorías
            return (
              <div className="section-card" key={-1}>
                {data.content.map((section, idx) => {
                  if (filterItems(section?.items).length === 0) return null;
                  return (<div key={idx}>
                    <h6 className="section-title">{i18nValue(section, "title", language)}</h6>
                    <p className="section-description">{i18nValue(section, "description", language)}</p>
                    {section.items && filterItems(section.items).map(item => {
                      if (item) {
                        return <Item key={item.id} reg={item} />
                      }
                      return false;
                    })}
                  </div>
                )})}
              </div>
            );
          } else if (currentFilter.type === "category") {
            // Mostrar solo la categoría seleccionada
            const section = data.content[currentFilter.categoryIndex];
            if (filterItems(section?.items).length === 0) return null;
            return (
              <div className="section-card" key={currentFilter.categoryIndex}>
                <h5 className="section-title">{i18nValue(section, "title", language)}</h5>
                <p className="section-description">{i18nValue(section, "description", language)}</p>
                {section.items && filterItems(section.items).map(item => {
                  if (item) {
                    return <Item key={item.id} reg={item} />
                  }
                  return false;
                })}
              </div>
            );
          } else if (currentFilter.type === "tag") {
            // Mostrar todas las categorías pero solo items con la etiqueta seleccionada
            return (
              <div className="section-card" key={-1}>
                {data.content.map((section, idx) => {
                  const filteredItems = filterItems(section.items || []);
                  if (filteredItems.length === 0) return null;
                  return (
                    <div key={idx}>
                      <h6 className="section-title">{i18nValue(section, "title", language)}</h6>
                      <p className="section-description">{i18nValue(section, "description", language)}</p>
                      {filteredItems.map(item => {
                        if (item) {
                          return <Item key={item.id} reg={item} />
                        }
                        return false;
                      })}
                    </div>
                  );
                })}
              </div>
            );
          }
        })()
      )}
    </Screen>
  );
}